// src/pages/CoursesDashboardPage.jsx
// 🎓 TICKET KQXTR7 - Dashboard de cursos disponibles (Beta)
// 🎓 TICKET NVDLP4 - Botón "Volver a dashboard" agregado
// 🎓 TICKET VMCJD4 - Consume API real de cursos publicados (elimina CourseCatalog hardcode)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowRight, ArrowLeft, Clock, Award, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { getPublishedCourses } from '../services/coursesApi';


const CoursesDashboardPage = () => {
  const navigate = useNavigate();
  
  // 🎓 TICKET VMCJD4 - Estados para consumir API real
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🎓 TICKET VMCJD4 - Cargar cursos desde API
  const loadCourses = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getPublishedCourses();
      setCourses(data);
      console.log('✅ VMCJD4: Cursos cargados desde API', data);
    } catch (err) {
      console.error('❌ VMCJD4: Error cargando cursos:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar cursos al montar
  useEffect(() => {
    loadCourses();
  }, []);

  // Mapeo de colores por courseId (puede expandirse)
  const courseColors = {
    'design-thinking': 'from-green-500 to-emerald-600',
    'decision-support': 'from-blue-500 to-cyan-600',
    'negotiation-mastery': 'from-purple-500 to-pink-600',
    'default': 'from-gray-500 to-slate-600'
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 🎓 TICKET NVDLP4 - Botón volver a dashboard */}
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a dashboard
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              Cursos disponibles (Beta)
            </h1>
          </div>
          <p className="text-gray-600">
            Explora los cursos organizados por frentes de aprendizaje
          </p>
        </div>

        {/* 🎓 TICKET VMCJD4 - Estado Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
            <span className="text-lg text-gray-600 font-medium">Cargando cursos...</span>
          </div>
        )}

        {/* 🎓 TICKET VMCJD4 - Estado Error */}
        {!isLoading && error && (
          <div className="max-w-md mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                No se pudieron cargar los cursos
              </h3>
              <p className="text-red-700 mb-4">
                Ocurrió un error al conectar con el servidor. Verifica tu conexión e intenta nuevamente.
              </p>
              <button
                onClick={loadCourses}
                className="flex items-center gap-2 mx-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* 🎓 TICKET VMCJD4 - Estado Vacío */}
        {!isLoading && !error && courses.length === 0 && (
          <div className="max-w-md mx-auto text-center py-20">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay cursos publicados disponibles
            </h3>
            <p className="text-gray-500">
              Los cursos aparecerán aquí cuando estén publicados por los administradores.
            </p>
          </div>
        )}

        {/* 🎓 TICKET VMCJD4 - Grid de cursos desde API */}
        {!isLoading && !error && courses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => {
              const colorGradient = courseColors[course.courseId] || courseColors.default;
              const visibleSkills = (course.skills || []).slice(0, 3);
              const remainingSkills = (course.skills || []).length - 3;

              return (
                <div
                  key={course.courseId}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/courses/${course.courseId}`)}
                >
                  {/* Header con gradiente */}
                  <div className={`bg-gradient-to-r ${colorGradient} p-6 text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 text-6xl opacity-20 -mr-4 -mt-2">
                      📚
                    </div>
                    <h3 className="text-2xl font-bold relative z-10">
                      {course.title}
                    </h3>
                  </div>

                  {/* Body */}
                  <div className="p-6 flex flex-col h-64">
                    <p className="text-gray-600 mb-4 leading-relaxed flex-grow line-clamp-3">
                      {course.shortDescription}
                    </p>

                    {/* Metadata */}
                    <div className="space-y-3 mb-4">
                      {/* Duración */}
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>{course.estimatedHours} horas estimadas</span>
                      </div>

                      {/* Skills */}
                      {course.skills && course.skills.length > 0 && (
                        <div className="flex items-start gap-2 text-sm text-gray-500">
                          <Award className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div className="flex flex-wrap gap-1">
                            {visibleSkills.map((skill, idx) => (
                              <span key={idx} className="inline-block">
                                {skill}
                                {idx < visibleSkills.length - 1 && ','}
                              </span>
                            ))}
                            {remainingSkills > 0 && (
                              <span className="font-semibold text-blue-600">
                                +{remainingSkills}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-end gap-2 text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                        <span>Ver temario</span>
                        <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Mensaje informativo */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            ℹ️ <strong>Versión Beta:</strong> Esta es una nueva experiencia de navegación por cursos. 
            El dashboard original sigue disponible en <span className="font-mono">/dashboard</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoursesDashboardPage;

